import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Link2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { LoadingSpinner } from '../ui/loading-states';
import { useEmployees } from '../../hooks/useEmployees';
import { useMapBioTerminalCard } from '../../hooks/useBio';
import type {
  BioTerminalCard,
  MapBioTerminalCardResult,
} from '../../types/bio.types';

interface MapTerminalCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: BioTerminalCard | null;
  onMapped: (result: MapBioTerminalCardResult) => void;
}

const errorMessage = (error: unknown): string => {
  const apiError = error as { message?: string } | null;
  return apiError?.message || 'Не удалось сопоставить карточку';
};

export function MapTerminalCardDialog({
  open,
  onOpenChange,
  card,
  onMapped,
}: MapTerminalCardDialogProps) {
  const [search, setSearch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const mapCard = useMapBioTerminalCard();

  // Имя с устройства — обычно лучшая подсказка для поиска сотрудника.
  useEffect(() => {
    if (!open) return;
    setSearch(card?.name ?? '');
    setEmployeeId('');
    mapCard.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card]);

  const { data: employees, isLoading } = useEmployees({
    search: search.trim() || undefined,
    isActive: true,
    limit: 20,
  });

  const options = useMemo(() => employees?.data ?? [], [employees]);

  const handleSubmit = async () => {
    if (!card || !employeeId) return;
    try {
      const result = await mapCard.mutateAsync({
        cardId: card.id,
        employeeId,
      });
      onMapped(result);
      onOpenChange(false);
    } catch {
      // Текст ошибки показывается ниже — диалог остаётся открытым.
    }
  };

  if (!card) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div>
            <DialogTitle>Сопоставить карточку</DialogTitle>
            <DialogDescription>
              Проходы этого номера начнут попадать в табель — переснимать
              человека не нужно.
            </DialogDescription>
          </div>
          {!mapCard.isPending && (
            <DialogClose onClose={() => onOpenChange(false)} />
          )}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md bg-muted p-3 text-sm">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <span>
                <span className="text-muted-foreground">Номер: </span>
                <span className="font-mono break-all">
                  {card.terminal_employee_no}
                </span>
              </span>
              <span>
                <span className="text-muted-foreground">
                  Имя на устройстве:{' '}
                </span>
                {card.name || '—'}
              </span>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {card.cards.length > 1
                ? `Сопоставится сразу на терминалах (${card.cards.length}):`
                : 'Терминал:'}
            </div>
            <ul className="mt-1 space-y-0.5 text-xs">
              {card.cards.map((entry) => (
                <li key={entry.id} className="flex items-center gap-1 break-all">
                  {entry.employee && (
                    <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-green-600" />
                  )}
                  {entry.terminal?.serial_number || '—'}
                  {entry.employee ? ' — уже сопоставлена' : ''}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <Label htmlFor="map-card-search">Найти сотрудника</Label>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="map-card-search"
                className="pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Фамилия или имя"
                disabled={mapCard.isPending}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="map-card-employee">Сотрудник</Label>
            {isLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <select
                id="map-card-employee"
                className="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                disabled={mapCard.isPending}
              >
                <option value="">— выберите сотрудника —</option>
                {options.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.last_name} {employee.first_name}
                    {employee.department?.name
                      ? ` · ${employee.department.name}`
                      : ''}
                  </option>
                ))}
              </select>
            )}
            {!isLoading && options.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Никого не найдено — уточните поиск
              </p>
            )}
          </div>

          {mapCard.error && (
            <div className="flex items-start gap-2 rounded-md bg-red-100 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{errorMessage(mapCard.error)}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mapCard.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!employeeId || mapCard.isPending}
          >
            <Link2 className="mr-2 h-4 w-4" />
            Сопоставить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
